import type { FormInputComponent } from '../types'

/**
 * Normalizes form components from backend model to admin mental model.
 *
 * Current transformations:
 * - Unwraps template containers from repeatables (admin sees fields directly)
 * - Type casts custom props to Record<string, unknown>
 *
 * This is the single place where backend → admin transformations happen,
 * establishing a clear separation between physical storage and logical editing model.
 *
 * @param components - Raw components from backend or external source
 * @returns Normalized components ready for admin editing
 */
export function normalizeFormForAdmin(components: unknown[]): FormInputComponent[] {
	return components.map((component) => normalizeComponent(component as FormInputComponent))
}

/**
 * Recursively normalizes a single component and its children.
 */
function normalizeComponent(component: FormInputComponent): FormInputComponent {
	// Type cast custom props (currently done inline in FormBuilder)
	const normalized: FormInputComponent = {
		...component,
		custom: component.custom as Record<string, unknown>,
	}

	// Handle repeatable components: keep template container but normalize its children
	if (normalized.name === 'repeatable' && normalized.children) {
		// Recursively normalize children (including the template container)
		normalized.children = normalized.children.map((child) => normalizeComponent(child))
	}
	// Recursively normalize children for other component types
	else if (normalized.children && normalized.children.length > 0) {
		normalized.children = normalized.children.map((child) => normalizeComponent(child))
	}

	return normalized
}
